import React, { useEffect, useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Switch } from "../ui/switch";
import { ShieldCheck, Timer, BellRing } from "lucide-react";

const Fact = ({ icon: Icon, text, testId }) => (
  <div className="flex items-start gap-2 text-xs text-slate-600" data-testid={testId}>
    <Icon size={14} className="mt-0.5 text-cyan-700 flex-shrink-0" />
    <span>{text}</span>
  </div>
);

export function OrgSecurityCard() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/security/settings").then((r) => setSettings(r.data)).catch(() => toast.error("Could not load security settings"));
  }, []);

  const toggle = async (value) => {
    setSaving(true);
    try {
      const r = await apiClient.put("/security/settings", { require_mfa_all: value });
      setSettings((s) => ({ ...s, ...r.data }));
      toast.success(value ? "MFA is now required for everyone" : "MFA is now required for admins only");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update setting");
    } finally { setSaving(false); }
  };

  if (!settings) return null;

  return (
    <Card data-testid="org-security-card">
      <CardHeader>
        <CardTitle>Organisation Security</CardTitle>
        <CardDescription>Sign-in rules that apply to everyone in your organisation</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between p-4 border rounded-sm gap-4">
          <div>
            <p className="font-medium text-sm">Require MFA for everyone</p>
            <p className="text-xs text-slate-500 mt-1">Users without an authenticator app must set one up at their next sign-in. Admins always need MFA.</p>
          </div>
          <Switch checked={settings.require_mfa_all} onCheckedChange={toggle} disabled={saving} data-testid="require-mfa-all-switch" />
        </div>
        <div className="space-y-2">
          <Fact icon={ShieldCheck} text="Admin accounts always sign in with MFA." testId="fact-admin-mfa" />
          <Fact icon={Timer} text={`Users are signed out after ${settings.idle_timeout_minutes} minutes of inactivity.`} testId="fact-idle-timeout" />
          <Fact icon={BellRing} text="Admins and the affected user are emailed when an account is locked or signs in from a new device." testId="fact-login-alerts" />
        </div>
      </CardContent>
    </Card>
  );
}
